/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { ChartProps } from '../../Chart.props';
import { CHART_LEGEND_ALIGNMENT_TYPE_BOTTOM, ChartLegendAlignmentType } from '../../types/ChartLegendAlignment.types';
import {
  LINE_CHART_HEADER_POSITION,
  LineChartHeaderPosition,
  LineChartSeriesConfig,
  Threshold,
} from './LineChart.defs';

/**
 * The props for `<LineChart />`.
 */
export interface LineChartProps extends ChartProps {
  /**
   * The position of the legend.
   */
  alignLegend?: ChartLegendAlignmentType;

  /**
   * The label of the X axis.
   */
  axisXLabel?: string;

  /**
   * The label of the Y axis.
   */
  axisYLabel?: string;

  /**
   * Defines if chart lines (axisY, axisX) are bolder and of black color (axisY, axisX, grid-lines).
   */
  boldLines?: boolean;

  /**
   * Defines position of header content (title/subtitle).
   */
  headerPosition?: keyof LineChartHeaderPosition;

  /**
   * The array of labels to show on the X axis.
   */
  labels: Array<string>;

  /**
   * Hide the legend.
   */
  hideLegend?: boolean;
  /**
   * The array of identifiers to match the values.
   */
  legend?: Array<string>;

  /**
   * The options for the markers.
   */

  markerOptions?: unknown;
  /**
   * The maximum value for the X axis.
   */

  maxX?: number;

  /**
   * The minimum value for the X axis.
   */

  minX?: number;

  /**
   * Defines if chart should have a grid.
   */
  showGrid?: boolean;

  /**
   * Determine whether to show a legend.
   */
  showLegend?: boolean;

  /**
   * Enable smooth line between data points.
   */
  smooth?: boolean;

  /**
   * Set the target threshold(s) / goal line(s).
   */
  thresholds?: Array<Threshold>;

  /**
   * Adds tick marks on both axis of the chart.
   */

  tickMarks?: boolean;

  /**
   * The unit of measurement for the values on Y axis and for tooltip to display.
   */
  unit?: string;

  /**
   * Display units on the axis.
   */
  displayUnitsOnAxis?: boolean;

  /**
   * The data to plot and its config.
   */
  plottingOptions: Array<LineChartSeriesConfig>;

  /**
   * Hide tooltip on hover of the series.
   */
  hideTooltip?: boolean;
}

/**
 * The default prop values for `LineChart`.
 */
export const lineChartDefaults = {
  alignLegend: CHART_LEGEND_ALIGNMENT_TYPE_BOTTOM as ChartLegendAlignmentType,
  headerPosition: LINE_CHART_HEADER_POSITION.center,
  legend: [],
  showGrid: false,
  showLegend: true,
  smooth: true,
  thresholds: [],
  unit: '',
};
